import { format } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import {
  addMonths,
  getMonthGridRange,
  getViewerTimeZone,
  isToday,
  type LocalDate,
  localDateKey,
  toNativeDate,
} from '../utils/calendarDate';

interface CalendarMiniMonthProps {
  /** The currently selected day; also seeds which month opens. */
  anchor: LocalDate;
  onSelectDay: (date: LocalDate) => void;
  timeZone?: string;
  /** Injectable for tests; defaults to the real current instant. */
  now?: Date;
}

const WEEK_STARTS_ON = 0 as const;
const DAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * A compact month for jumping to a date without leaving the current view.
 *
 * It browses independently of the anchor: paging to next month here does not
 * move the calendar, only picking a day does. That is what makes it a picker
 * rather than a second set of period controls.
 */
export function CalendarMiniMonth({
  anchor,
  onSelectDay,
  timeZone = getViewerTimeZone(),
  now = new Date(),
}: Readonly<CalendarMiniMonthProps>) {
  const [visibleMonth, setVisibleMonth] = useState<LocalDate>(anchor);
  const weeks = getMonthGridRange(visibleMonth.year, visibleMonth.month, WEEK_STARTS_ON);
  const anchorKey = localDateKey(anchor);

  return (
    <VStack spacing="sm" testID="calendar_mini_month">
      <HStack align="center" justify="between">
        <Pressable
          testID="calendar_mini_prev"
          accessibilityRole="button"
          accessibilityLabel={format(toNativeDate(addMonths(visibleMonth, -1)), 'MMMM yyyy')}
          onPress={() => setVisibleMonth(addMonths(visibleMonth, -1))}
          style={styles.navButton}
        >
          <ChevronLeft size={16} color={colors.text.secondary} strokeWidth={2} />
        </Pressable>

        <Text variant="bodySmall" color={colors.text.primary} style={styles.monthLabel}>
          {format(toNativeDate(visibleMonth), 'MMMM yyyy')}
        </Text>

        <Pressable
          testID="calendar_mini_next"
          accessibilityRole="button"
          accessibilityLabel={format(toNativeDate(addMonths(visibleMonth, 1)), 'MMMM yyyy')}
          onPress={() => setVisibleMonth(addMonths(visibleMonth, 1))}
          style={styles.navButton}
        >
          <ChevronRight size={16} color={colors.text.secondary} strokeWidth={2} />
        </Pressable>
      </HStack>

      <HStack>
        {DAY_INITIALS.map((initial, index) => (
          <Box
            // biome-ignore lint/suspicious/noArrayIndexKey: weekday position is the identity of this fixed, never-reordered 7-item row.
            key={`${initial}-${index}`}
            style={styles.cell}
            alignItems="center"
            justifyContent="center"
          >
            <Text variant="caption" color={colors.text.tertiary}>
              {initial}
            </Text>
          </Box>
        ))}
      </HStack>

      {weeks.map((week) => (
        <HStack key={localDateKey(week[0])}>
          {week.map((date) => {
            const key = localDateKey(date);
            const selected = key === anchorKey;
            const outsideMonth = date.month !== visibleMonth.month;

            return (
              <Pressable
                key={key}
                testID={`calendar_mini_day_${key}`}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                accessibilityLabel={format(toNativeDate(date), 'EEEE, MMMM d, yyyy')}
                onPress={() => onSelectDay(date)}
                style={[
                  styles.cell,
                  selected && { backgroundColor: colors.primary[500] },
                  !selected && isToday(date, timeZone, now) && styles.today,
                ]}
              >
                <Text
                  variant="caption"
                  // A token, never an opacity: adjacent-month days at 30%
                  // alpha measure 1.79:1, and they are still real tappable
                  // dates rather than decoration.
                  color={
                    selected
                      ? colors.text.inverse
                      : outsideMonth
                        ? colors.neutral[500]
                        : colors.text.primary
                  }
                >
                  {date.day}
                </Text>
              </Pressable>
            );
          })}
        </HStack>
      ))}
    </VStack>
  );
}

const styles = StyleSheet.create({
  navButton: {
    minWidth: tokens.interaction.minimumTarget.default,
    minHeight: tokens.interaction.minimumTarget.default,
    alignItems: 'center',
    justifyContent: 'center',
  },
  monthLabel: {
    fontWeight: '600',
  },
  cell: {
    flex: 1,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.sm,
  },
  today: {
    borderWidth: 1,
    borderColor: colors.primary.borderActive,
  },
});
